import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../lib/iotApi';
import { clientCreationApi } from '../../../lib/clientCreationApi';

export const receiptStorageKey = (user: { id: string; org_id?: string | null }) =>
  `client-creation-pending:${JSON.stringify([user.id, user.org_id])}`;

export function savedCreation(key: string): string | null {
  try {
    const value = sessionStorage.getItem(key);
    return value && /^apply-[a-f0-9]{24}$/.test(value) ? value : null;
  } catch { return null; }
}

export default function ResumeClientCreation({ applyId, storageKey, initialFailure = false }: { applyId: string; storageKey: string; initialFailure?: boolean }) {
  const { t } = useTranslation('admin_client_onboarding');
  const [discardable, setDiscardable] = useState(false);
  const [discarded, setDiscarded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(initialFailure);
  const [done, setDone] = useState(false);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const resume = async () => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setFailed(false);
    try {
      let item = await clientCreationApi.inspect(applyId);
      if (!mounted.current) return;
      if (['STALE', 'FAILED_CONFLICT'].includes(item.status)
        || (item.status === 'CONFIRMED' && item.confirmation_expired === true)) {
        setDiscardable(true); throw new Error('New confirmation required');
      }
      if (!['APPLIED', 'VERIFIED'].includes(item.status)) item = await clientCreationApi.apply(item);
      if (!mounted.current) return;
      if (!['APPLIED', 'VERIFIED'].includes(item.status)) throw new Error('Incomplete creation');
      item = await clientCreationApi.verify(item);
      if (!mounted.current) return;
      if (item.status !== 'VERIFIED') throw new Error('Incomplete verification');
      try { sessionStorage.removeItem(storageKey); } catch { /* Server remains authoritative. */ }
      setDone(true);
    } catch (error) {
      if (mounted.current) {
        setFailed(true);
        if (error instanceof ApiError && error.code === 'onboarding_not_found') setDiscardable(true);
      }
    }
    finally { inFlight.current = false; if (mounted.current) setBusy(false); }
  };
  const discard = () => {
    try {
      // Keep an audit pointer even when removing the active recovery block.
      sessionStorage.setItem(`${storageKey}:archived:${applyId}`, applyId);
      if (sessionStorage.getItem(storageKey) === applyId) sessionStorage.removeItem(storageKey);
      setDiscarded(true);
    } catch { setFailed(true); }
  };
  if (discarded) return <a href="/admin/clients/new">{t('creation.startOver')}</a>;
  return <section className="rounded-2xl border border-[var(--border-light)] p-5 space-y-4">
    <h1 className="text-xl font-semibold">{done ? t('creation.success') : t('creation.resumeTitle')}</h1>
    <p>{done ? t('creation.nextStep') : t('creation.resumeHelp')}</p>
    {failed && <p role="alert">{t('creation.failed')}</p>}
    {!done && <button type="button" disabled={busy} onClick={resume}
      className="min-h-[44px] rounded-xl bg-[var(--color-primary)] text-white px-4">
      {busy ? t('creation.busy') : t('creation.retry')}</button>}
    {discardable && !busy && <button type="button" onClick={discard}>{t('creation.startOver')}</button>}
    <a href="/admin/clients" className="block min-h-[44px] underline">{t('creation.openClients')}</a>
  </section>;
}
